// The recycler's pre-orders (market Orders page, dashboard preview, overview
// counts). Fulfilment is always computed from the linked pickups exactly as the
// backend does (pre_order/helper/fulfillment.py), so linking, unlinking,
// cancelling or deleting moves every number on every page together.

import type { RecyclerPreOrdersOverview } from '../../services/recycler-dashboard.service';
import {
  PreOrderAmountUnit,
  PreOrderStatus,
  type PreOrderDetail,
  type PreOrderPagination,
  type PreOrderRequest,
  type PreOrderSchema,
  type UpdatePreOrderRequest,
} from '../../types/pre-order.type';
import {
  DEMO_MATERIAL_IDS,
  DEMO_RECYCLER_ORG_ID,
  HOUR_MS,
  daysAgo,
  ids,
  iso,
  materialById,
  paginate,
  type DemoPickupOrder,
} from '../core';
import { isInBasket } from './basket';
import { classificationSnapshot, regulatoryMappings } from './catalog';
import { fail } from './errors';
import { findOrganization, isRecyclerOrg } from './organizations';
import { allPickups, countsAsReceived, pickupById, pickupFulfilledKg, toLinkedPickup } from './pickups';

// ── Units and fulfilment maths (mirrors the backend) ───────────────────────

const KG_PER_UNIT: Record<string, number> = {
  kg: 1,
  tons: 1000,
  lbs: 0.453592,
  bags: 13.6,
  cartons: 0.5,
  bottles: 0.025,
  pallets: 680,
  cubic_meters: 400,
  liters: 0.4,
};

/** convert_to_kg: unknown units (cubic yards, gallons) pass through unchanged. */
export function toKg(amount: number, unit: string | null | undefined): number {
  return amount * (KG_PER_UNIT[unit ?? 'kg'] ?? 1);
}

const COMPLETION_TOLERANCE_KG = 0.1;
const AMOUNT_UNITS = new Set<string>(Object.values(PreOrderAmountUnit));

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

function isFulfilledKg(orderedKg: number, fulfilledKg: number): boolean {
  return orderedKg > 0 && fulfilledKg >= orderedKg - COMPLETION_TOLERANCE_KG;
}

// ── Records ────────────────────────────────────────────────────────────────

export interface PreOrderRecord {
  id: string;
  origin: 'recycler' | 'ops';
  organization_id: string;
  material_id: string;
  material_type_id: string;
  material_category: string;
  regulatory_code_id: string | null;
  material_name_snapshot: string;
  category_name_snapshot: string;
  regulatory_code_snapshot: string | null;
  regulatory_code_description_snapshot: string | null;
  mwan_source_category_id: string | null;
  mwan_source_category_snapshot: string | null;
  physical_state_snapshot: string | null;
  amount: number;
  amount_unit: PreOrderAmountUnit;
  data: Record<string, unknown>;
  status: PreOrderStatus;
  created_at: string;
  updated_at: string;
  linked_pickup_ids: string[];
}

let records: PreOrderRecord[] = [];
let nextSeq = 1;

function linkedOf(rec: PreOrderRecord): DemoPickupOrder[] {
  return rec.linked_pickup_ids.map(pickupById).filter((p): p is DemoPickupOrder => Boolean(p));
}

export interface PreOrderMetrics {
  amount_kg: number;
  fulfilled_kg: number;
  remaining_kg: number;
  fulfillment_percent: number;
}

export function metricsOf(rec: PreOrderRecord): PreOrderMetrics {
  const orderedKg = toKg(rec.amount, rec.amount_unit);
  const fulfilledKg = linkedOf(rec).reduce((sum, p) => sum + pickupFulfilledKg(p), 0);
  let remainingKg = Math.max(0, orderedKg - fulfilledKg);
  let percent = orderedKg > 0 ? (fulfilledKg / orderedKg) * 100 : 0;
  if (isFulfilledKg(orderedKg, fulfilledKg)) {
    remainingKg = 0;
    percent = Math.max(percent, 100);
  }
  return {
    amount_kg: round2(orderedKg),
    fulfilled_kg: round2(fulfilledKg),
    remaining_kg: round2(remainingKg),
    fulfillment_percent: round2(percent),
  };
}

/** recompute_pre_order_status: cancelled stays; otherwise derived from fulfilment. */
function computedStatus(rec: PreOrderRecord): PreOrderStatus {
  if (rec.status === PreOrderStatus.CANCELLED) return PreOrderStatus.CANCELLED;
  const orderedKg = toKg(rec.amount, rec.amount_unit);
  const fulfilledKg = linkedOf(rec).reduce((sum, p) => sum + pickupFulfilledKg(p), 0);
  if (isFulfilledKg(orderedKg, fulfilledKg)) return PreOrderStatus.COMPLETED;
  if (fulfilledKg > 0) return PreOrderStatus.PARTIALLY_FULFILLED;
  return PreOrderStatus.PENDING;
}

export function toPreOrderSchema(rec: PreOrderRecord): PreOrderSchema {
  const m = metricsOf(rec);
  const org = findOrganization(rec.organization_id);
  return {
    id: rec.id,
    origin: rec.origin,
    material_category: rec.material_category,
    material_id: rec.material_id,
    material_type_id: rec.material_type_id,
    waste_stream_profile_id: null,
    regulatory_code_id: rec.regulatory_code_id,
    material_name_snapshot: rec.material_name_snapshot,
    category_name_snapshot: rec.category_name_snapshot,
    regulatory_code_snapshot: rec.regulatory_code_snapshot,
    regulatory_code_description_snapshot: rec.regulatory_code_description_snapshot,
    mwan_source_category_id: rec.mwan_source_category_id,
    mwan_source_category_snapshot: rec.mwan_source_category_snapshot,
    physical_state_snapshot: rec.physical_state_snapshot,
    amount: rec.amount,
    amount_unit: rec.amount_unit,
    organization_id: rec.organization_id,
    data: { ...rec.data, origin: rec.origin },
    status: rec.status,
    created_at: rec.created_at,
    updated_at: rec.updated_at,
    amount_kg: m.amount_kg,
    fulfilled_kg: m.fulfilled_kg,
    remaining_kg: m.remaining_kg,
    fulfillment_percent: m.fulfillment_percent,
    linked_pickup_count: rec.linked_pickup_ids.length,
    organization: org ? { id: org.id, name: org.name } : null,
    material_type_ids: [rec.material_type_id],
  };
}

export function toPreOrderDetail(rec: PreOrderRecord): PreOrderDetail {
  const m = metricsOf(rec);
  const linked = linkedOf(rec)
    .slice()
    .sort((a, b) => b.created_at.localeCompare(a.created_at));
  return {
    ...toPreOrderSchema(rec),
    ...m,
    linked_pickups: linked.map(toLinkedPickup),
  };
}

// ── Seed: 8 pre-orders, linked pickups drawn from the core completed pickups ─

type SeedStatus = 'completed' | 'partial' | 'pending' | 'cancelled';

interface SeedSpec {
  code: string;
  status: SeedStatus;
  daysAgo: number;
  hour: number;
  unit: PreOrderAmountUnit;
  /** In `unit`. For completed orders this is the volume to reach; the final amount is set from the linked weight. */
  amount: number;
  origin: 'recycler' | 'ops';
  notes: string;
}

/** Oldest first: ids.preOrder(1..8) follow creation order. */
const SEEDS: SeedSpec[] = [
  {
    code: 'aluminium_cans',
    status: 'completed',
    daysAgo: 150,
    hour: 6.25,
    unit: PreOrderAmountUnit.KG,
    amount: 550,
    origin: 'recycler',
    notes: 'Used beverage cans, crushed or loose. For the UBC bale export lot.',
  },
  {
    code: 'occ',
    status: 'completed',
    daysAgo: 118,
    hour: 7.5,
    unit: PreOrderAmountUnit.KG,
    amount: 10000,
    origin: 'recycler',
    notes: 'Baled OCC for the Sulay line 2 pulper. Bales up to 450 kg, moisture under 12%.',
  },
  {
    code: 'pet',
    status: 'partial',
    daysAgo: 60,
    hour: 8.75,
    unit: PreOrderAmountUnit.KG,
    amount: 3000,
    origin: 'recycler',
    notes: 'Clear PET bottles only, caps on is fine. For the flake line.',
  },
  {
    code: 'occ',
    status: 'partial',
    daysAgo: 45,
    hour: 10.5,
    unit: PreOrderAmountUnit.TONS,
    amount: 20,
    origin: 'ops',
    notes: 'Raised by ops to cover the Q4 commitment to the Riyadh paper mill.',
  },
  {
    code: 'steel_scrap',
    status: 'cancelled',
    daysAgo: 30,
    hour: 11.25,
    unit: PreOrderAmountUnit.KG,
    amount: 5000,
    origin: 'recycler',
    notes: 'Cancelled: the mill switched to direct delivery from the scrap yard.',
  },
  {
    code: 'hdpe',
    status: 'pending',
    daysAgo: 6,
    hour: 7.25,
    unit: PreOrderAmountUnit.KG,
    amount: 1500,
    origin: 'recycler',
    notes: 'Natural and coloured HDPE containers, rinsed. Deliver to Sulay Sorting Plant.',
  },
  {
    code: 'aluminium_cans',
    status: 'pending',
    daysAgo: 3,
    hour: 9.5,
    unit: PreOrderAmountUnit.KG,
    amount: 900,
    origin: 'ops',
    notes: 'Ops top-up for next month\'s UBC shipment.',
  },
  {
    code: 'occ',
    status: 'pending',
    daysAgo: 1,
    hour: 6.75,
    unit: PreOrderAmountUnit.TONS,
    amount: 15,
    origin: 'recycler',
    notes: 'Weekly OCC top-up for the pulper. Any branch, baled.',
  },
];

function seedAmount(spec: SeedSpec, linkedKg: number): number {
  const factor = KG_PER_UNIT[spec.unit] ?? 1;
  const targetKg = spec.amount * factor;
  if (spec.status === 'completed' && linkedKg > 0) {
    // Ordered volume a little under what arrived, rounded the way people order.
    const stepKg = spec.unit === PreOrderAmountUnit.TONS ? 500 : linkedKg >= 2000 ? 500 : 50;
    const kg = Math.max(stepKg, Math.floor(linkedKg / stepKg) * stepKg);
    return round2(kg / factor);
  }
  if (spec.status === 'partial' && linkedKg >= targetKg * 0.95) {
    const kg = Math.ceil(linkedKg / 0.6 / 500) * 500;
    return round2(kg / factor);
  }
  return spec.amount;
}

function seedLinks(spec: SeedSpec, materialId: string, createdAt: string, used: Set<string>): string[] {
  if (spec.status !== 'completed' && spec.status !== 'partial') return [];
  const candidates = allPickups()
    .filter((p) => countsAsReceived(p) && p.material_id === materialId && !used.has(p.id))
    .sort((a, b) => (a.completed_at ?? '').localeCompare(b.completed_at ?? ''));
  const after = candidates.filter((p) => p.created_at > createdAt);
  const pool = after.length > 0 ? after : candidates;
  const targetKg = toKg(spec.amount, spec.unit);
  const picked: DemoPickupOrder[] = [];
  let sum = 0;
  if (spec.status === 'completed') {
    for (const p of pool) {
      if (sum >= targetKg) break;
      picked.push(p);
      sum += pickupFulfilledKg(p);
    }
  } else {
    for (const p of pool) {
      if (sum >= targetKg * 0.45) break;
      const kg = pickupFulfilledKg(p);
      if (sum + kg < targetKg * 0.8) {
        picked.push(p);
        sum += kg;
      }
    }
    if (picked.length === 0 && pool.length > 0) picked.push(pool[0]);
  }
  picked.forEach((p) => used.add(p.id));
  return picked.map((p) => p.id);
}

function buildSeedRecords(): PreOrderRecord[] {
  const used = new Set<string>();
  return SEEDS.map((spec, i) => {
    const materialId = DEMO_MATERIAL_IDS[spec.code];
    const snapshot = classificationSnapshot(materialId);
    if (!snapshot) throw new Error(`[demo] unknown seed material ${spec.code}`);
    const created = new Date(daysAgo(spec.daysAgo).getTime() + spec.hour * HOUR_MS);
    const createdAt = iso(created);
    const linked = seedLinks(spec, materialId, createdAt, used);
    const linkedKg = linked.reduce((sum, id) => sum + pickupFulfilledKg(pickupById(id)!), 0);
    const lastArrival = linked
      .map((id) => pickupById(id)?.completed_at ?? createdAt)
      .reduce((latest, at) => (at > latest ? at : latest), createdAt);
    const updatedAt =
      spec.status === 'cancelled' ? iso(new Date(created.getTime() + 6 * 24 * HOUR_MS + 3 * HOUR_MS)) : lastArrival;
    const seq = i + 1;
    const rec: PreOrderRecord = {
      id: ids.preOrder(seq),
      origin: spec.origin,
      organization_id: DEMO_RECYCLER_ORG_ID,
      material_id: snapshot.material_id,
      material_type_id: snapshot.material_type_id,
      material_category: snapshot.material_category,
      regulatory_code_id: snapshot.regulatory_code_id,
      material_name_snapshot: snapshot.material_name_snapshot,
      category_name_snapshot: snapshot.category_name_snapshot,
      regulatory_code_snapshot: snapshot.regulatory_code_snapshot,
      regulatory_code_description_snapshot: snapshot.regulatory_code_description_snapshot,
      mwan_source_category_id: snapshot.mwan_source_category_id,
      mwan_source_category_snapshot: snapshot.mwan_source_category_snapshot,
      physical_state_snapshot: snapshot.physical_state_snapshot,
      amount: seedAmount(spec, linkedKg),
      amount_unit: spec.unit,
      data: {
        reference: `RGR-PO-${createdAt.slice(0, 4)}-${String(40 + seq).padStart(4, '0')}`,
        notes: spec.notes,
      },
      status: spec.status === 'cancelled' ? PreOrderStatus.CANCELLED : PreOrderStatus.PENDING,
      created_at: createdAt,
      updated_at: updatedAt,
      linked_pickup_ids: linked,
    };
    rec.status = computedStatus(rec);
    return rec;
  });
}

export function resetPreOrders(): void {
  records = buildSeedRecords();
  nextSeq = records.length + 1;
}

// ── Queries ────────────────────────────────────────────────────────────────

export function allPreOrderRecords(): readonly PreOrderRecord[] {
  return records;
}

export function findPreOrder(id: string): PreOrderRecord | undefined {
  return records.find((r) => r.id === id);
}

function requirePreOrder(id: string): PreOrderRecord {
  const rec = findPreOrder(id);
  if (!rec) fail(404, 'Pre order not found');
  return rec;
}

/** Every pickup attached to some pre-order (for the unlinked-only incoming list). */
export function linkedPickupIds(): Set<string> {
  return new Set(records.flatMap((r) => r.linked_pickup_ids));
}

export function openPreOrderCount(orgId: string, materialId: string): number {
  return records.filter(
    (r) =>
      r.organization_id === orgId &&
      r.material_id === materialId &&
      (r.status === PreOrderStatus.PENDING || r.status === PreOrderStatus.PARTIALLY_FULFILLED),
  ).length;
}

function parseDate(value: unknown): number | null {
  if (typeof value !== 'string' || !value) return null;
  const t = Date.parse(value);
  return Number.isFinite(t) ? t : null;
}

export interface PreOrderListQuery {
  organization_id?: string;
  page?: unknown;
  limit?: unknown;
  sort_by?: string;
  sort_pre_order?: string;
  material_category?: string;
  status?: string;
  start_date?: string;
  end_date?: string;
  search?: string;
}

type SortableKey = 'created_at' | 'updated_at' | 'amount' | 'status' | 'material_category';
const SORTABLE = new Set<string>(['created_at', 'updated_at', 'amount', 'status', 'material_category']);

export function listPreOrders(q: PreOrderListQuery): PreOrderPagination {
  const start = parseDate(q.start_date);
  const end = parseDate(q.end_date);
  const search = q.search?.trim().toLowerCase();
  const rows = records
    .filter((r) => !q.organization_id || r.organization_id === q.organization_id)
    .filter((r) => !q.material_category || r.material_category === q.material_category)
    .filter((r) => !q.status || r.status === q.status)
    .filter((r) => start === null || Date.parse(r.created_at) >= start)
    .filter((r) => end === null || Date.parse(r.created_at) <= end)
    .filter((r) => {
      if (!search) return true;
      const haystack = [
        r.material_category,
        r.material_name_snapshot,
        r.category_name_snapshot,
        r.regulatory_code_snapshot,
        r.origin,
        JSON.stringify(r.data),
      ]
        .join(' ')
        .toLowerCase();
      return haystack.includes(search);
    });

  if (q.sort_by && SORTABLE.has(q.sort_by)) {
    const key = q.sort_by as SortableKey;
    const dir = q.sort_pre_order === 'desc' ? -1 : 1;
    rows.sort((a, b) => {
      const av = a[key];
      const bv = b[key];
      if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * dir;
      return String(av).localeCompare(String(bv)) * dir;
    });
  } else {
    rows.sort((a, b) => b.created_at.localeCompare(a.created_at));
  }

  const page = paginate(rows, q.page ?? 1, q.limit ?? 20);
  return {
    pre_orders: page.items.map(toPreOrderSchema),
    total: page.total,
    page: page.page,
    limit: page.limit,
  };
}

export function preOrderDetail(id: string): PreOrderDetail {
  return toPreOrderDetail(requirePreOrder(id));
}

const EMPTY_OVERVIEW: RecyclerPreOrdersOverview = {
  total_orders: 0,
  total_ordered_kg: 0,
  fulfilled_kg: 0,
  remaining_kg: 0,
  pending_count: 0,
  partially_fulfilled_count: 0,
  completed_count: 0,
  cancelled_count: 0,
};

function inWindow(rec: PreOrderRecord, start: number | null, end: number | null): boolean {
  const t = Date.parse(rec.created_at);
  // end_date is inclusive here and in listPreOrders, so both read a window the same way.
  return (start === null || t >= start) && (end === null || t <= end);
}

export function preOrdersOverview(orgId: string, startDate?: unknown, endDate?: unknown): RecyclerPreOrdersOverview {
  const start = parseDate(startDate);
  const end = parseDate(endDate);
  return records
    .filter((r) => r.organization_id === orgId && inWindow(r, start, end))
    .reduce<RecyclerPreOrdersOverview>((acc, r) => {
      const m = metricsOf(r);
      return {
        total_orders: acc.total_orders + 1,
        total_ordered_kg: round2(acc.total_ordered_kg + m.amount_kg),
        fulfilled_kg: round2(acc.fulfilled_kg + m.fulfilled_kg),
        remaining_kg: round2(acc.remaining_kg + m.remaining_kg),
        pending_count: acc.pending_count + (r.status === PreOrderStatus.PENDING ? 1 : 0),
        partially_fulfilled_count:
          acc.partially_fulfilled_count + (r.status === PreOrderStatus.PARTIALLY_FULFILLED ? 1 : 0),
        completed_count: acc.completed_count + (r.status === PreOrderStatus.COMPLETED ? 1 : 0),
        cancelled_count: acc.cancelled_count + (r.status === PreOrderStatus.CANCELLED ? 1 : 0),
      };
    }, EMPTY_OVERVIEW);
}

/** GET /dashboard/recycler/:org/pre-orders: overview plus detail rows, newest first. */
export function preOrdersDashboard(orgId: string, q: { start_date?: unknown; end_date?: unknown; offset?: unknown; limit?: unknown }) {
  const start = parseDate(q.start_date);
  const end = parseDate(q.end_date);
  const rows = records
    .filter((r) => r.organization_id === orgId && inWindow(r, start, end))
    .sort((a, b) => b.created_at.localeCompare(a.created_at));
  const offset = Math.max(0, Number.parseInt(String(q.offset ?? 0), 10) || 0);
  const limitRaw = Number.parseInt(String(q.limit ?? ''), 10);
  const limit = Number.isFinite(limitRaw) && limitRaw > 0 ? limitRaw : rows.length;
  return {
    organization_id: orgId,
    start_date: typeof q.start_date === 'string' ? q.start_date : null,
    end_date: typeof q.end_date === 'string' ? q.end_date : null,
    total_count: rows.length,
    pre_orders: rows.slice(offset, offset + limit).map(toPreOrderDetail),
    pre_orders_truncated: false,
    overview: preOrdersOverview(orgId, q.start_date, q.end_date),
  };
}

// ── Mutations ──────────────────────────────────────────────────────────────

function touch(rec: PreOrderRecord, patch: Partial<PreOrderRecord>): PreOrderRecord {
  const next: PreOrderRecord = { ...rec, ...patch, updated_at: iso(new Date()) };
  next.status = computedStatus(next);
  records = records.map((r) => (r.id === rec.id ? next : r));
  return next;
}

function validAmount(value: unknown): number {
  const amount = Number(value);
  if (!Number.isFinite(amount) || amount <= 0) fail(422, 'Amount must be greater than zero');
  return amount;
}

function validUnit(value: unknown): PreOrderAmountUnit {
  if (value === undefined || value === null || value === '') return PreOrderAmountUnit.KG;
  if (typeof value !== 'string' || !AMOUNT_UNITS.has(value)) fail(422, `Unsupported amount unit: ${String(value)}`);
  return value as PreOrderAmountUnit;
}

function snapshotFor(orgId: string, materialId: string | undefined, regulatoryCodeId: string | undefined) {
  if (!materialId) fail(422, 'Choose a waste type from the catalog');
  const material = materialById(materialId);
  if (!material) fail(422, 'Unknown waste type');
  if (!isInBasket(orgId, materialId)) {
    fail(422, `'${material.name_en}' is not one of this organization's waste types. Add it on the Materials page first.`);
  }
  if (regulatoryCodeId && !regulatoryMappings(material).some((m) => m.regulatory_code_id === regulatoryCodeId)) {
    fail(422, `That EWC code does not apply to ${material.name_en}`);
  }
  const snapshot = classificationSnapshot(materialId, regulatoryCodeId);
  if (!snapshot) fail(422, 'Unknown waste type');
  return snapshot;
}

export function createPreOrder(req: Partial<PreOrderRequest>): PreOrderSchema {
  const orgId = req.organization_id;
  if (!orgId || !findOrganization(orgId)) fail(404, 'Organization not found');
  if (!isRecyclerOrg(orgId)) fail(403, 'Not allowed to create pre order for this organization');
  const snapshot = snapshotFor(orgId, req.material_id ?? req.material_type_id, req.regulatory_code_id);
  const amount = validAmount(req.amount);
  const unit = validUnit(req.amount_unit);
  const now = iso(new Date());
  const data = { ...(req.data ?? {}) } as Record<string, unknown>;
  const origin = data.origin === 'ops' ? 'ops' : 'recycler';
  delete data.origin;
  const rec: PreOrderRecord = {
    id: ids.preOrder(nextSeq),
    origin,
    organization_id: orgId,
    ...snapshot,
    physical_state_snapshot: req.physical_state || snapshot.physical_state_snapshot,
    amount,
    amount_unit: unit,
    data,
    status: PreOrderStatus.PENDING,
    created_at: now,
    updated_at: now,
    linked_pickup_ids: [],
  };
  nextSeq += 1;
  records = [rec, ...records];
  return toPreOrderSchema(rec);
}

export function updatePreOrder(id: string, req: Partial<UpdatePreOrderRequest>): PreOrderSchema {
  const rec = requirePreOrder(id);
  const patch: Partial<PreOrderRecord> = {};
  if (req.data && typeof req.data === 'object') {
    const data = { ...req.data } as Record<string, unknown>;
    delete data.origin;
    patch.data = data;
  }
  if (req.amount !== undefined) patch.amount = validAmount(req.amount);
  if (req.amount_unit !== undefined) patch.amount_unit = validUnit(req.amount_unit);
  const nextMaterial = req.material_id ?? req.material_type_id;
  if (nextMaterial && nextMaterial !== rec.material_id) {
    if (rec.linked_pickup_ids.length > 0) {
      fail(400, 'Cannot change the waste type of a pre order that already has linked pickups');
    }
    Object.assign(patch, snapshotFor(rec.organization_id, nextMaterial, req.regulatory_code_id));
  } else if (req.regulatory_code_id && req.regulatory_code_id !== rec.regulatory_code_id) {
    Object.assign(patch, snapshotFor(rec.organization_id, rec.material_id, req.regulatory_code_id));
  }
  if (req.physical_state) patch.physical_state_snapshot = req.physical_state;
  return toPreOrderSchema(touch(rec, patch));
}

/** PATCH /pre_order/:id/status, with the backend's rules. */
export function setPreOrderStatus(id: string, status: unknown): PreOrderSchema {
  const rec = requirePreOrder(id);
  if (status === PreOrderStatus.CANCELLED) {
    const next: PreOrderRecord = { ...rec, status: PreOrderStatus.CANCELLED, updated_at: iso(new Date()) };
    records = records.map((r) => (r.id === id ? next : r));
    return toPreOrderSchema(next);
  }
  if (
    status === PreOrderStatus.PENDING ||
    status === PreOrderStatus.PARTIALLY_FULFILLED ||
    status === PreOrderStatus.COMPLETED
  ) {
    const computed = computedStatus(rec);
    if (computed !== status) {
      fail(400, `Cannot set status to ${status}; computed status is ${computed}`);
    }
    return toPreOrderSchema(touch(rec, {}));
  }
  fail(400, 'Unsupported status transition');
}

export function deletePreOrder(id: string): void {
  requirePreOrder(id);
  // Its linked pickups are released with it and show up as incoming again.
  records = records.filter((r) => r.id !== id);
}

export function linkPickups(id: string, pickupIds: unknown): PreOrderDetail {
  const rec = requirePreOrder(id);
  if (!Array.isArray(pickupIds) || pickupIds.length === 0) fail(422, 'pickup_order_ids must list at least one pickup');
  if (rec.status === PreOrderStatus.CANCELLED || rec.status === PreOrderStatus.COMPLETED) {
    fail(400, `Cannot link pickup to pre order with status ${rec.status}`);
  }
  const owner = new Map<string, string>();
  records.forEach((r) => r.linked_pickup_ids.forEach((pid) => owner.set(pid, r.id)));
  const toAdd: string[] = [];
  for (const raw of pickupIds) {
    const pickupId = String(raw);
    const pickup = pickupById(pickupId);
    if (!pickup) fail(404, `Pickup order not found: ${pickupId}`);
    const existing = owner.get(pickupId);
    if (existing && existing !== rec.id) fail(409, 'Pickup order is already linked to another pre order');
    if (pickup.recycler_org_id !== rec.organization_id) {
      fail(400, 'Pickup recycler does not match pre order organization');
    }
    const material = materialById(pickup.material_id);
    if (material && material.category_code !== rec.material_category) {
      fail(400, 'Pickup material parent category does not match pre order');
    }
    if (rec.material_id && pickup.material_id !== rec.material_id) {
      fail(400, "Pickup waste type does not match the pre order's waste type");
    }
    if (!existing && !toAdd.includes(pickupId)) toAdd.push(pickupId);
  }
  const next = touch(rec, { linked_pickup_ids: [...rec.linked_pickup_ids, ...toAdd] });
  return toPreOrderDetail(next);
}

export function unlinkPickup(id: string, pickupId: string): PreOrderDetail {
  const rec = requirePreOrder(id);
  if (!rec.linked_pickup_ids.includes(pickupId)) fail(404, 'Pickup is not linked to this pre order');
  const next = touch(rec, { linked_pickup_ids: rec.linked_pickup_ids.filter((pid) => pid !== pickupId) });
  return toPreOrderDetail(next);
}
